/**
 * Keeping a live mission list correct under two writers — the `mission_event`
 * stream and the poll backstop — is one problem, and it is now solved in one
 * place. Extracted verbatim from the dispatch board (`page.tsx`) when the COP
 * missions panel became the second surface holding such a list; both consume
 * these helpers, so the stream/poll race can only ever be fixed (or broken)
 * once.
 */
import type { MissionEventPayload } from '@/hooks/useWebSocket';
import type { MissionListItem } from './missionListShared';

/**
 * Fold a freshly arrived list into what the event stream has already applied.
 *
 * The poll and the stream can disagree exactly one way: a request issued at T returns the
 * board as it stood at T, so any event applied after T describes a change that response has
 * never heard of. Re-applying such a row keeps a mission another operator just started from
 * blinking back to `scheduled` for one poll interval.
 *
 * Everything else is settled — a poll that STARTED after the event necessarily saw the
 * committed row, so the server's copy is taken and the override is dropped. That bounds an
 * override to a single poll cycle, so a row can never be pinned to a stale local copy.
 */
export function reconcile(
  polled: MissionListItem[],
  current: MissionListItem[],
  eventApplied: Map<string, number>,
  startedAtMs: number,
): { missions: MissionListItem[]; eventApplied: Map<string, number> } {
  const raced = new Map([...eventApplied].filter(([, appliedAtMs]) => appliedAtMs >= startedAtMs));
  if (raced.size === 0) return { missions: polled, eventApplied: new Map() };

  const localById = new Map(current.map((m) => [m.id, m]));
  // A raced id the poll still lists keeps the local row; one it lists but we no longer hold
  // was deleted by an event and stays deleted.
  const missions = polled.flatMap((m) => (raced.has(m.id) ? (localById.get(m.id) ?? []) : [m]));
  const polledIds = new Set(polled.map((m) => m.id));
  for (const id of raced.keys()) {
    if (polledIds.has(id)) continue;
    const local = localById.get(id);
    if (local) missions.push(local); // created, or moved onto the board, after the poll went out
  }
  return { missions, eventApplied: raced };
}

/**
 * A field the frame has no opinion about keeps the value the row already had (#420).
 *
 * THE ONE PLACE A REPLACE WOULD BE A DOWNGRADE. Every other column on the frame is read
 * straight off the `missions` row, so the server always has an answer and the newest one wins.
 * The two coverage decorations are looked up separately and can fail on their own, and the
 * server deliberately sends `undefined` rather than `false` when they do — because `false` here
 * is not a smaller claim, it is the opposite claim: `coverage_held_open: false` puts the green
 * IN PROGRESS badge and a running "Elapsed since launch" clock on a mission whose aircraft is
 * parked, and `coverage_attached: false` re-offers a delete the server answers with a 409.
 *
 * `??` and not `||`, so an authoritative `false` still replaces. Only absence preserves.
 */
function withOmittedFieldsCarriedOver(
  previous: MissionListItem,
  incoming: MissionListItem,
): MissionListItem {
  return {
    ...incoming,
    coverage_attached: incoming.coverage_attached ?? previous.coverage_attached,
    coverage_held_open: incoming.coverage_held_open ?? previous.coverage_held_open,
    // THE ORBIT FIELDS JOIN THE SAME BELT (#700 review). They are optional on
    // `MissionListItem` exactly as the coverage pair was, so a frame omitting them type-checks
    // cleanly here and strips `r 500 m - CW` off the row - which is #420's defect, on the one
    // field that describes the airspace an orbiting aircraft is occupying. The server now always
    // sends them; this is what stops an older server, or the next added field, from blanking a
    // row that had them.
    // `in`, not `??`, and the two pairs genuinely differ (#700 review). The server sends
    // `undefined` for a coverage fact it could not READ and a real boolean otherwise, so `??`
    // is right there. For the orbit pair it sends an explicit `null` for every non-orbit, and
    // `null ?? previous` would carry a stale radius forward - convert an orbit to a route (which
    // clears the columns in the same request) and the row would keep `orbit_radius_m: 500`,
    // contradicting its own `type`, for the rest of the session. Only ABSENCE may preserve.
    orbit_radius_m: 'orbit_radius_m' in incoming ? incoming.orbit_radius_m : previous.orbit_radius_m,
    orbit_direction: 'orbit_direction' in incoming ? incoming.orbit_direction : previous.orbit_direction,
  };
}

/**
 * Apply one `mission_event` frame to a mission list scoped to `visibleStatuses`.
 *
 * Typed as the list's own row on purpose: if the wire shape ever drifts from what these
 * surfaces render, it fails here at compile time rather than at 03:00 on a live board.
 * Deleted, or moved to a status this list does not show, removes the row; anything else
 * upserts it in place — carrying over any coverage fact the frame did not answer.
 *
 * THAT COMPILE-TIME CLAIM HAS A KNOWN LIMIT, and #420 was it: both coverage fields are OPTIONAL
 * on `MissionListItem`, so a frame carrying neither type-checked cleanly here and silently
 * stripped them off every row it touched. The server now always sends both, and
 * `withOmittedFieldsCarriedOver` is the belt to that braces - a field the frame omits can no longer
 * blank a row that had it.
 */
export function applyMissionEvent(
  missions: MissionListItem[],
  event: MissionEventPayload,
  visibleStatuses: readonly string[],
): MissionListItem[] {
  const row: MissionListItem | null = event.kind === 'deleted' ? null : event.mission;
  if (!row || !visibleStatuses.includes(row.status)) {
    return missions.filter((m) => m.id !== event.mission_id);
  }
  const known = missions.some((m) => m.id === row.id);
  return known
    ? missions.map((m) => (m.id === row.id ? withOmittedFieldsCarriedOver(m, row) : m))
    : [...missions, row];
}
